/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import type {
	AccessRole,
	ViewAccessLevel,
} from '@jira/polaris__domain-view/src/ViewAccessLevel.types.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createContainer,
	createStore,
	createHook,
	createActionsHook,
	type Action,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type OverrideContainerComponent,
	type Store,
} from '@atlassian/react-sweet-state';
import { loadPermissions } from './loadPermissions.tsx';
import {
	loadViewPermissionsForCurrentUser,
	type LoadViewPermissionsForCurrentUserState,
} from './loadViewPermissionsForCurrentUser.tsx';
import { switchPreviewContributorMode } from './switchPreviewContributorMode.tsx';
import { setContributorCreateIssuePermission } from './setContributorCreateIssuePermission.tsx';
import type { State, Props } from './State.types.tsx';

export type Actions = {
	readonly loadPermissions: () => Action<State, Props>;
	readonly switchPreviewContributorMode: (isPreviewOn: boolean) => Action<State, Props>;
	readonly setContributorCreateIssuePermission: (enabled: boolean) => Action<State, Props>;
	readonly loadViewPermissionsForCurrentUser: (containerId: string) => Action<
		State,
		Props,
		Promise<{
			role: AccessRole | undefined;
			accessLevel: ViewAccessLevel | undefined;
			state: LoadViewPermissionsForCurrentUserState;
		}>
	>;
};

const actions: Actions = {
	loadPermissions,
	switchPreviewContributorMode,
	setContributorCreateIssuePermission,
	loadViewPermissionsForCurrentUser,
};

const initialState: State = {
	meta: {
		loaded: false,
		loading: false,
		error: undefined,
	},
	permissionsByProjectId: {},
	contributorPermissions: [
		{ key: 'EDIT_OWN_COMMENTS', havePermission: true },
		{ key: 'ADD_COMMENTS', havePermission: true },
		{ key: 'DISCOVERY_VOTE', havePermission: true },
		{ key: 'CREATE_ISSUES', havePermission: true },
		{ key: 'CREATE_ATTACHMENTS', havePermission: true },
		{ key: 'DELETE_OWN_ATTACHMENTS', havePermission: true },
		{ key: 'CREATE_DISCOVERY_INSIGHTS', havePermission: true },
		{ key: 'CREATE_DISCOVERY_VIEW_COMMENTS', havePermission: true },
	],
	viewPermissions: {},
	isPreviewContributorMode: false,
};

export const PermissionsStore: Store<State, Actions, unknown> = createStore<State, Actions>({
	initialState,
	actions,
	name: 'PolarisPermissionsStore',
});

export const usePermissions: HookFunction<State, BoundActions<State, Actions>, void> = createHook(
	PermissionsStore,
);
export const usePermissionActions: HookActionsFunction<BoundActions<State, Actions>> =
	createActionsHook(PermissionsStore);

export const PermissionsContainer: OverrideContainerComponent<Props> = createContainer<
	State,
	Actions,
	Props
>(PermissionsStore, {
	onInit:
		() =>
		({ dispatch }) => {
			dispatch(loadPermissions());
		},
	onUpdate:
		() =>
		({ dispatch }) =>
			dispatch(loadPermissions()),
});

/**
 * @deprecated use src/packages/polaris/component-permissions-store/src/controllers/permissions/hooks/useIsPreviewContributorMode.tsx
 */
export const useIsPreviewContributorMode: HookFunction<
	boolean,
	BoundActions<State, Actions>,
	void
> = createHook(PermissionsStore, {
	selector: (state) => state.isPreviewContributorMode,
});
